<template>
  <div class="license-id-editor">
    <label v-if="showLabel" class="form-label">License ID</label>
    <div class="d-flex flex-row align-items-center gap-1">
      <input
        v-if="isNew"
        v-model="model.licenseId"
        type="text"
        class="form-control flex-grow-1"
        autocomplete="off"
        placeholder="Auto-generated if left blank"
      />
      <template v-else>
        <input
          ref="input"
          v-model="draftLicenseId"
          type="text"
          class="form-control flex-grow-1"
          autocomplete="off"
          :disabled="!editing || saving"
          @keydown.enter.prevent="commit"
          @keydown.escape.prevent="cancel"
        />
        <template v-if="editable">
          <button
            v-if="!editing"
            type="button"
            class="btn btn-flat btn-icon flex-shrink-0"
            title="Edit license ID"
            aria-label="Edit license ID"
            @click="beginEditing">
            <i class="bi-pencil" />
          </button>
          <template v-else>
            <button
              type="button"
              class="btn btn-flat btn-icon flex-shrink-0 text-success"
              title="Save license ID"
              aria-label="Save license ID"
              :disabled="!canSave"
              @click="commit">
              <SpinnerView v-if="saving" small />
              <i v-else class="bi-check-lg" />
            </button>
            <button
              type="button"
              class="btn btn-flat btn-icon flex-shrink-0"
              title="Cancel license ID edit"
              aria-label="Cancel license ID edit"
              :disabled="saving"
              @click="cancel">
              <i class="bi-x-lg" />
            </button>
          </template>
        </template>
      </template>
    </div>
    <div v-if="isNew" class="form-text">
      Leave blank to generate the ID automatically.
    </div>
    <div v-if="error" class="small text-danger mt-1" role="alert">
      {{ error.message || String(error) }}
    </div>
  </div>
</template>

<script>
import { LicenseEditorModel } from '@/models/LicenseEditorModel';
import { License } from '@/models/License';
import SpinnerView from '@/components/common/SpinnerView.vue';

export default {
  components: { SpinnerView },
  emits: ['saved'],
  props: {
    model: {
      type: LicenseEditorModel,
      default: null
    },
    license: {
      type: License,
      default: null
    },
    editable: Boolean,
    showLabel: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      draftLicenseId: '',
      editing: false,
      saving: false,
      error: null
    };
  },
  computed: {
    isNew() {
      return Boolean(this.model?.isNew);
    },
    currentLicenseId() {
      return this.model?.licenseId
        || this.model?.id
        || this.license?.number
        || this.license?.id
        || '';
    },
    normalizedDraftLicenseId() {
      return String(this.draftLicenseId || '').trim();
    },
    canSave() {
      return !this.saving
        && Boolean(this.normalizedDraftLicenseId)
        && this.normalizedDraftLicenseId !== this.currentLicenseId;
    }
  },
  watch: {
    currentLicenseId: {
      immediate: true,
      handler() {
        if (!this.editing) {
          this.resetDraft();
        }
      }
    }
  },
  methods: {
    resetDraft() {
      this.draftLicenseId = this.currentLicenseId;
    },
    beginEditing() {
      this.error = null;
      this.resetDraft();
      this.editing = true;
      this.$nextTick(() => {
        this.$refs.input?.focus();
        this.$refs.input?.select();
      });
    },
    cancel() {
      this.error = null;
      this.resetDraft();
      this.editing = false;
    },
    async commit() {
      if (!this.canSave) {
        return;
      }

      this.saving = true;
      this.error = null;
      try {
        const source = this.model || this.license;
        if (!source) {
          throw new Error('No license was provided');
        }
        const license = await source.updateLicenseId(this.normalizedDraftLicenseId);
        this.draftLicenseId = license?.data?.licenseId || license?.id || this.currentLicenseId;
        this.$emit('saved', license);
      } catch (error) {
        this.error = error;
        this.resetDraft();
      } finally {
        this.saving = false;
        this.editing = false;
      }
    }
  }
};
</script>
